/**
 * Constants and types for the global search page (`components/Search/`). The response and
 * row-state types live in `components/Models/GlobalSearchResults.Models.tsx`.
 */
import { MatchingProduct } from "@ryerson/frontend.common";

/**
 * Shortest term that starts a search on `/search` (`Search.tsx`). Also the minimum for the
 * part-number lookups in `Shared/QuickEntry/QuickEntryV2.tsx` and
 * `Shared/BulkUploadModal/BulkUploadHelpers.tsx`, so a change applies there too.
 */
export const SEARCH_MIN_LENGTH = 3;
/**
 * Milliseconds. `Search.tsx` passes it to `SearchInputSection` as `delayTime`, which does not
 * read it: the search box submits on Enter or the Search button.
 */
export const SEARCH_DEBOUNCE_DELAY = 900;

/**
 * Product / Form / Shape filter names → the product field each reads. Used for the option
 * counts in `FiltersComponent/ChildComponents/FilterOptions/FilterOptions.tsx`.
 */
export enum KeyMapper {
	"Product" = "category",
	"Form" = "form",
	"Shape" = "shape",
}

/**
 * The result tabs, as URL hashes. The hash is the tab's state: `Search.tsx` reads it on
 * `hashchange` / `popstate`, and an unknown hash means Products.
 */
export enum UrlHashTabs {
	Products = "#products",
	ProductCategory = "#product-category",
	TheGauge = "#the-gauge",
	Locations = "#locations",
	Other = "#other",
}

/** One article or resource from the search response's `articlesResources`. */
export interface ArticleResourceItem {
	blogCategory: string;
	excerpt: string;
	category: string;
	image?: string;
	link: string;
	title: string;
}
/** Used only by {@link ArticlesTabsContent}. */
export type ResourceArray = Array<ArticleResourceItem>;

/** Articles split by tab, for the tab counts in `SearchTabToggle.tsx`. */
export type ArticlesTabsContent = {
	theGauge: ResourceArray;
	location: ResourceArray;
	other: ResourceArray;
};
/** `category` values that put an article in The Gauge or Locations; any other is Other. */
export enum ArticleTypes {
	theGauge = "blog",
	location = "location",
}

/** A search result with its position in the combined result list. */
export interface UpdatedMatchingProduct extends MatchingProduct {
	/**
	 * 0-based position in recently purchased + matching products
	 * (`HelperFunctions.tsx` → `getSearchResults`). Page pricing and delivery-time lines send
	 * it as `index + 1` (`HelperFunctions.tsx` → `getItemsForPricingRequestBody`).
	 */
	index: number;
}
